import React, { useState } from "react";
import OTPInput from "react-otp-input";
import { authApi } from "../../api/auth";

interface MfaVerificationProps {
  email: string;
  onSuccess: (token: string, refreshToken: string, user: unknown) => void;
  onCancel: () => void;
}

export const MfaVerification: React.FC<MfaVerificationProps> = ({
  email,
  onSuccess,
  onCancel,
}) => {
  const [token, setToken] = useState("");
  const [backupCode, setBackupCode] = useState("");
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await authApi.verifyMfa({
        email,
        token: useBackupCode ? undefined : token,
        backupCode: useBackupCode ? backupCode : undefined,
      });

      onSuccess(response.token, response.refreshToken!, response.user);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { error?: string } } };
      setError(error.response?.data?.error || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            Two-Factor Authentication
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
            Enter the verification code from your authenticator app
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/20 p-4">
              <p className="text-sm text-red-800 dark:text-red-400">{error}</p>
            </div>
          )}

          {!useBackupCode ? (
            <div className="space-y-4">
              <label
                htmlFor="token"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 text-center mb-4"
              >
                Verification Code
              </label>
              <div className="flex justify-center">
                <OTPInput
                  value={token}
                  onChange={setToken}
                  numInputs={6}
                  renderSeparator={<span className="mx-1 md:mx-2"></span>}
                  renderInput={(props) => (
                    <input
                      {...props}
                      className="!w-10 !h-12 md:!w-12 md:!h-14 text-center text-xl md:text-2xl font-semibold border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-white outline-none transition-all"
                      style={{
                        width: "40px",
                        height: "48px",
                      }}
                    />
                  )}
                  shouldAutoFocus
                  inputType="tel"
                />
              </div>
            </div>
          ) : (
            <div>
              <label
                htmlFor="backupCode"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Backup Code
              </label>
              <input
                id="backupCode"
                name="backupCode"
                type="text"
                autoComplete="off"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 sm:text-sm"
                placeholder="XXXX-XXXX"
                value={backupCode}
                onChange={(e) => setBackupCode(e.target.value.toUpperCase())}
              />
            </div>
          )}

          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => {
                setUseBackupCode(!useBackupCode);
                setToken("");
                setBackupCode("");
                setError("");
              }}
              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-500 dark:hover:text-blue-300"
            >
              {useBackupCode ? "Use authenticator app" : "Use backup code"}
            </button>
          </div>

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={
                loading ||
                (!useBackupCode && token.length !== 6) ||
                (useBackupCode && !backupCode)
              }
              className="flex-1 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Verifying..." : "Verify"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
